/**
 * Abstract base class for API services.
 *
 * Provides basic CRUD operations for an API service.
 * Subclasses must implement the {@link createFromResponse} and {@link toRequest} methods
 * to define how the transformation from API response to entity and vice versa is done.
 * @class
 *
 * @template T The type of the entity handled by this service.
 * @template U The type of the request/response object used by the API.
 */

import { HttpClient } from "@angular/common/http";
import { Subject } from "rxjs";
import { EntityInterface } from "src/app/model/Table";

export abstract class AbstractApiService<T extends EntityInterface> {
  /**
   * HTTP client for making API requests.
   */
  protected abstract _http: HttpClient;

  /**
   * Subject to emit updates to the entity collection.
   */
  protected abstract _subject: Subject<T[]>;

  /**
   * Current collection of entities.
   */
  protected abstract _collection?: T[];

  /**
   * Adds, modifies or deletes an entity from the entity collection (doesn't mutate).
   *
   * @param entityToSave - The entity to be added, modified, or deleted.
   * @param action - The action to perform ('create', 'modify', or 'delete').
   *                 Defaults to 'create'.
   */
  protected refresh(entityToSave: T, action: ('create' | 'modify' | 'delete') = 'create'): void {
    if (this._collection) {
      if (action === 'create') {
        this._collection = [...this._collection.map(entity => entity.copyOf() as T), entityToSave];
      } else if (action === 'modify') {
        this._collection = this._collection.map(entity => entity.id === entityToSave.id ? entityToSave.copyOf() as T : entity.copyOf() as T);
      } else if (action === 'delete') {
        this._collection = this._collection.filter(entity => entity.id !== entityToSave.id);
      }
      this._subject.next(this._collection.map(entity => entity.copyOf() as T));
    }
  }
}

